import React, { useState, useEffect } from 'react';
import { Wallet, Plus, History, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import ClientAuthService from '../services/clientAuthService';

const WalletBalance = ({ onTopUp, onViewHistory, refreshTrigger }) => {
  const [balance, setBalance] = useState(0);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadWalletBalance();
  }, [refreshTrigger]);

  const loadWalletBalance = async () => {
    try {
      setLoading(true);
      const response = await ClientAuthService.getWalletBalance();
      console.log('Wallet balance response:', response);
      setBalance(response.data.balance);
      setStatistics(response.data.statistics);
    } catch (error) {
      setError('Failed to load wallet balance');
      console.error('Wallet balance error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow">
        <div className="p-6 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h5 className="text-lg font-semibold text-gray-900 flex items-center">
          <Wallet className="mr-2" size={20} />
          Wallet Balance
        </h5>
      </div>
      <div className="p-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4" role="alert">
            {error}
          </div>
        )}
        
        <div className="text-center mb-6">
          <div className="text-4xl font-bold text-blue-600 mb-2">
            {formatCurrency(balance)}
          </div>
          <p className="text-gray-500 mb-0">Available Balance</p>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center">
            <TrendingUp className="text-green-500 mr-2" size={16} />
            <div>
              <div className="text-sm text-gray-500">Total Deposits</div>
              <div className="font-bold text-green-600">
                {formatCurrency(statistics.total_deposits || 0)}
              </div>
            </div>
          </div>
          <div className="flex items-center">
            <TrendingDown className="text-red-500 mr-2" size={16} />
            <div>
              <div className="text-sm text-gray-500">Total Spent</div>
              <div className="font-bold text-red-600">
                {formatCurrency(statistics.total_withdrawals || 0)}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <div className="text-sm text-gray-500">Deposits</div>
            <div className="font-bold">{statistics.deposit_count || 0}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Payments</div>
            <div className="font-bold">{statistics.withdrawal_count || 0}</div>
          </div>
        </div>

        <div className="space-y-2">
          <button 
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200 flex items-center justify-center"
            onClick={onTopUp}
          >
            <Plus size={16} className="mr-1" />
            Top Up Wallet
          </button>
          <button 
            className="w-full border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-md transition duration-200 flex items-center justify-center"
            onClick={onViewHistory}
          >
            <History size={16} className="mr-1" />
            View History
          </button>
        </div>
      </div>
    </div>
  );
};

export default WalletBalance;